import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Users, UsersRound, MessagesSquare, PhoneCall, ClipboardCheck, ListTree, KeyRound,
  ScrollText, Tag, Timer, ArrowRight, ShieldCheck, Activity, Inbox, TrendingUp, Sparkles, ThumbsUp,
} from "lucide-react";
import api from "@/lib/api";
import { DISPLAY_TZ } from "@/lib/app-utils";
import { useTenant } from "@/contexts/TenantContext";
import { useAuth } from "@/contexts/AuthContext";

// 4 headline operational metrics. Only tiles with a real detail destination
// are clickable; the rest are informational (no misleading redirect).
const PRIMARY = [
  { key: "active_calls",                 label: "Active calls",          icon: PhoneCall,      accent: "0 72% 51%",   to: "/admin/live-dashboard" },
  { key: "positive_dispositions_today",  label: "Positive dispositions", icon: ThumbsUp,       accent: "158 64% 40%", to: "/admin/reports/agent-performance", sub: "today" },
  { key: "conversations",                label: "Conversations",         icon: MessagesSquare, accent: "217 91% 60%" },
  { key: "open_inbox",                   label: "Unread inbox",          icon: Inbox,          accent: "38 92% 50%" },
];

// Denser configuration counts grouped under "System vitals".
const VITALS = [
  { key: "users",           label: "Users",          icon: Users,         to: "/admin/users" },
  { key: "groups",          label: "User groups",    icon: UsersRound,    to: "/admin/groups" },
  { key: "dispositions",    label: "Dispositions",   icon: Tag,           to: "/admin/dispositions" },
  { key: "pause_codes",     label: "Pause codes",    icon: Timer,         to: "/admin/pause-codes" },
  { key: "wrapup_codes",    label: "Wrap-up codes",  icon: ClipboardCheck,to: "/admin/wrapup-codes" },
  { key: "custom_fields",   label: "Custom fields",  icon: ListTree,      to: "/admin/crm-screens" },
  { key: "integrations",    label: "Integrations",   icon: KeyRound,      to: "/admin/integrations" },
  { key: "audit_events_24h",label: "Audit (24h)",    icon: ScrollText,    to: "/admin/audit-log" },
];

const GETTING_STARTED = [
  { label: "Add a SIP trunk", to: "/admin/sip-trunks" },
  { label: "Map inbound DIDs", to: "/admin/dids" },
  { label: "Build queues + SLA", to: "/admin/queues" },
  { label: "Connect channels", to: "/admin/channels" },
  { label: "Pick your LLM", to: "/admin/ai-suite" },
];

export default function AdminOverview() {
  const [data, setData] = useState({ counts: {}, integrations: [], recent_audit: [] });
  const [loading, setLoading] = useState(true);
  const { tenant } = useTenant();
  const { user } = useAuth();

  useEffect(() => {
    let alive = true;
    const load = (initial) =>
      api.get("/admin/overview")
        .then((r) => { if (alive) setData(r.data || {}); })
        .finally(() => { if (initial && alive) setLoading(false); });
    load(true);
    // Refresh periodically so "Active calls" (live queue/IVR/in-call) stays current.
    const t = setInterval(() => load(false), 15000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const firstName = (user?.name || "Administrator").split(" ")[0];

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-screen-2xl mx-auto">
      {/* Hero */}
      <div className="fade-in-up flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-7">
        <div>
          <div className="text-overline text-primary mb-1.5">Admin Console</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight" data-testid="admin-overview-title">
            {greeting}, {firstName}
          </h1>
          <p className="text-sm text-muted-foreground mt-1.5">
            {tenant?.name ? <>Managing <span className="font-semibold text-foreground">{tenant.name}</span> · </> : null}
            Here's what's happening across your contact center.
          </p>
        </div>
        <Badge variant="outline" className="gap-1.5 h-8 px-3 rounded-full self-start sm:self-auto border-emerald-500/40 text-emerald-600 dark:text-emerald-400" data-testid="overview-health-badge">
          <ShieldCheck className="w-3.5 h-3.5" /> All systems healthy
        </Badge>
      </div>

      {/* Primary KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {PRIMARY.map((b, i) => {
          const I = b.icon;
          const inner = (
            <Card className="relative overflow-hidden p-5 h-full border-border/60 group-hover:border-primary/40 transition-colors">
              <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full blur-2xl opacity-[0.12] group-hover:opacity-20 transition-opacity"
                   style={{ background: `hsl(${b.accent})` }} />
              <div className="flex items-center justify-between">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `hsl(${b.accent} / 0.12)`, color: `hsl(${b.accent})` }}>
                  <I className="w-[18px] h-[18px]" strokeWidth={2} />
                </div>
                {b.to && <ArrowRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />}
              </div>
              <div className="mt-4 font-mono font-bold text-4xl tracking-tight tabular-nums">
                {loading ? <span className="text-muted-foreground/40">—</span> : (data.counts?.[b.key] ?? 0)}
              </div>
              <div className="text-overline text-muted-foreground mt-1">{b.label}{b.sub ? ` · ${b.sub}` : ""}</div>
            </Card>
          );
          const cls = "fade-in-up group block";
          const style = { animationDelay: `${i * 60}ms` };
          return b.to ? (
            <Link key={b.key} to={b.to} data-testid={`kpi-${b.key}`} className={cls} style={style}>{inner}</Link>
          ) : (
            <div key={b.key} data-testid={`kpi-${b.key}`} className={cls} style={style}>{inner}</div>
          );
        })}
      </div>

      {/* Bento */}
      <div className="grid lg:grid-cols-3 gap-5 mt-5">
        {/* Recent activity — tall */}
        <Card className="lg:col-span-2 p-5 border-border/60 fade-in-up" style={{ animationDelay: "120ms" }}>
          <div className="flex items-center gap-2 mb-4">
            <Activity className="w-4 h-4 text-primary" />
            <h2 className="font-display font-bold text-lg">Recent activity</h2>
            <Link to="/admin/audit-log" className="ml-auto text-xs text-muted-foreground hover:text-primary transition-colors flex items-center gap-1" data-testid="overview-view-audit">
              View all <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          {(!data.recent_audit || data.recent_audit.length === 0) ? (
            <EmptyState icon={ScrollText} text="No recent activity yet." />
          ) : (
            <ul className="divide-y divide-border/60">
              {data.recent_audit.slice(0, 8).map((a) => (
                <li key={a.id} className="py-2.5 flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{a.action}</div>
                    <div className="text-xs text-muted-foreground">{a.actor_email || a.actor_id}</div>
                  </div>
                  <div className="text-[11px] font-mono text-muted-foreground whitespace-nowrap shrink-0">
                    {new Date(a.ts).toLocaleString([], { timeZone: DISPLAY_TZ, month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Right column */}
        <div className="space-y-5">
          {/* System vitals */}
          <Card className="p-5 border-border/60 fade-in-up" style={{ animationDelay: "160ms" }}>
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4 text-primary" />
              <h2 className="font-display font-bold text-lg">System vitals</h2>
            </div>
            <div className="divide-y divide-border/60">
              {VITALS.map((v) => {
                const I = v.icon;
                return (
                  <Link key={v.key} to={v.to} data-testid={`vital-${v.key}`}
                        className="flex items-center gap-2.5 py-2 group">
                    <I className="w-3.5 h-3.5 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                    <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">{v.label}</span>
                    <span className="ml-auto font-mono font-semibold text-sm tabular-nums">
                      {loading ? "—" : (data.counts?.[v.key] ?? 0)}
                    </span>
                  </Link>
                );
              })}
            </div>
          </Card>
        </div>
      </div>

      {/* Lower row: integrations + getting started */}
      <div className="grid lg:grid-cols-3 gap-5 mt-5">
        {/* Active integrations */}
        <Card className="lg:col-span-2 p-5 border-border/60 fade-in-up" style={{ animationDelay: "200ms" }}>
          <div className="flex items-center gap-2 mb-3">
            <KeyRound className="w-4 h-4 text-primary" />
            <h2 className="font-display font-bold text-lg">Active integrations</h2>
            <Badge variant="outline" className="ml-auto">{data.integrations?.length || 0}</Badge>
          </div>
          {(!data.integrations || data.integrations.length === 0) ? (
            <EmptyState icon={KeyRound} text="No integrations yet." action={{ label: "Open Integrations Vault", to: "/admin/integrations" }} />
          ) : (
            <ul className="divide-y divide-border/60">
              {data.integrations.slice(0, 8).map((i) => (
                <li key={i.id} className="py-2.5 flex items-center gap-3">
                  <Badge variant="outline" className="text-[10px] uppercase shrink-0">{i.category}</Badge>
                  <span className="text-sm font-medium truncate">{i.label}</span>
                  <span className="text-xs text-muted-foreground truncate">{i.provider}</span>
                  <span className="ml-auto shrink-0">
                    {i.is_enabled
                      ? <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/15">Enabled</Badge>
                      : <Badge variant="outline">Disabled</Badge>}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Getting started — tactile grain card */}
        <Card className="relative overflow-hidden p-5 border-border/60 bg-secondary text-secondary-foreground fade-in-up" style={{ animationDelay: "240ms" }}>
          <div className="absolute inset-0 grain opacity-40 pointer-events-none" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4" />
              <h2 className="font-display font-bold text-lg">Get set up</h2>
            </div>
            <p className="text-xs text-secondary-foreground/70 mb-4">Five steps to go live.</p>
            <ol className="space-y-1.5">
              {GETTING_STARTED.map((s, i) => (
                <li key={s.to}>
                  <Link to={s.to} className="flex items-center gap-2.5 text-sm rounded-md px-2 py-1.5 -mx-2 hover:bg-white/10 transition-colors group">
                    <span className="w-5 h-5 rounded-full bg-white/15 text-[11px] font-mono font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                    <span className="flex-1">{s.label}</span>
                    <ArrowRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                  </Link>
                </li>
              ))}
            </ol>
          </div>
        </Card>
      </div>
    </div>
  );
}

function EmptyState({ icon: Icon, text, action }) {
  return (
    <div className="relative rounded-lg border border-dashed border-border/60 py-10 flex flex-col items-center justify-center text-center overflow-hidden">
      <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
      <Icon className="w-6 h-6 text-muted-foreground/50 mb-2" />
      <p className="text-sm text-muted-foreground relative">{text}</p>
      {action && (
        <Button asChild variant="outline" size="sm" className="mt-3 rounded-full relative">
          <Link to={action.to}>{action.label}</Link>
        </Button>
      )}
    </div>
  );
}
